import React, { useState, useEffect } from 'react';
import { extractImageUrlsFromProduct } from '../utils/firestoreImageHelpers';
import './ImageCarousel.css';

const ImageCarousel = ({ 
  images = [], 
  alt = "Product images", 
  showThumbnails = true, 
  autoPlay = false, 
  autoPlayInterval = 3000,
  className = "",
  onImageClick = null 
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(autoPlay);
  const [imageLoadErrors, setImageLoadErrors] = useState(new Set());

  // Normalize incoming images into Storage URLs only
  // Use a data URI placeholder instead of hero image to avoid showing wrong images
  const FALLBACK_IMAGE = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjM3NSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjBmMGYwIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkltYWdlIExvYWRpbmcuLi48L3RleHQ+PC9zdmc+';
  
  // Helper function to check if a URL is a valid image URL
  const isValidImageUrl = (url) => {
    if (!url || typeof url !== 'string') return false;
    // Accept Firebase Storage URLs (https://firebasestorage), http/https URLs, or base64 data URLs
    return url.startsWith('https://') || 
           url.startsWith('http://') || 
           url.startsWith('data:image');
  };
  
  // Normalize images - extract only Storage URLs
  const normalizedImages = (() => {
    if (!images || images.length === 0) {
      return [FALLBACK_IMAGE];
    }

    // If images is a product object, extract URLs from it
    if (typeof images === 'object' && !Array.isArray(images) && images.id) {
      const urls = extractImageUrlsFromProduct(images);
      return urls.length > 0 ? urls : [FALLBACK_IMAGE];
    }

    // If images is an array
    if (Array.isArray(images)) {
      const validUrls = images
        .map((img) => {
          // String URL (Storage URL or base64 data URL)
          if (typeof img === 'string') {
            return isValidImageUrl(img) ? img : null;
          }
          
          // Object with imageUrl (Storage URL or base64)
          if (img && typeof img === 'object') {
            if (img.imageUrl && isValidImageUrl(img.imageUrl)) {
              return img.imageUrl;
            }
            if (img.url && isValidImageUrl(img.url)) {
              return img.url;
            }
            // Check for base64Data in object
            if (img.base64Data && typeof img.base64Data === 'string') {
              if (img.base64Data.startsWith('data:image')) {
                return img.base64Data;
              }
              // Convert base64 string to data URL
              return `data:image/jpeg;base64,${img.base64Data}`;
            }
          }
          
          return null;
        })
        .filter(Boolean);

      return validUrls.length > 0 ? validUrls : [FALLBACK_IMAGE];
    }

    return [FALLBACK_IMAGE];
  })();

  // Debug: Log the images after normalization// Auto-play functionality
  useEffect(() => {
    if (isAutoPlaying && normalizedImages.length > 1) {
      const interval = setInterval(() => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % normalizedImages.length);
      }, autoPlayInterval);
      return () => clearInterval(interval);
    }
  }, [isAutoPlaying, normalizedImages.length, autoPlayInterval]);

  // Pause auto-play on hover
  const handleMouseEnter = () => {
    if (autoPlay) {
      setIsAutoPlaying(false);
    }
  };

  const handleMouseLeave = () => {
    if (autoPlay) {
      setIsAutoPlaying(true);
    }
  };

  const goToPrevious = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? normalizedImages.length - 1 : prevIndex - 1
    );
  };

  const goToNext = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % normalizedImages.length);
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  const handleImageError = (imageUrl, index) => {
    console.error('🚨 ImageCarousel - Image failed to load:', imageUrl, 'at index:', index);
    console.error('🚨 ImageCarousel - Image type:', typeof imageUrl);
    setImageLoadErrors(prev => new Set([...prev, index]));
  };

  const handleImageLoad = (imageUrl, index) => {setImageLoadErrors(prev => {
      const newSet = new Set(prev);
      newSet.delete(index);
      return newSet;
    });
  };

  if (!normalizedImages || normalizedImages.length === 0) {
    return (
      <div className={`image-carousel ${className}`}>
        <div className="carousel-main">
          <div className="carousel-image-container">
            <div className="no-image-placeholder">
              <span>No images available</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (normalizedImages.length === 1) {
    // First image in product detail (above the fold) should be eager
    const isAboveFold = className.includes('product-detail') || className.includes('product-image');
    return (
      <div className={`image-carousel ${className}`}>
        <div className="carousel-main">
          <div className="carousel-image-container">
            <img
              src={normalizedImages[0]}
              alt={alt}
              className={`carousel-image ${imageLoadErrors.has(0) ? 'error' : ''}`}
              loading={isAboveFold ? "eager" : "lazy"}
              decoding="async"
              fetchpriority={isAboveFold ? "high" : "auto"}
              onClick={() => onImageClick && onImageClick(normalizedImages[0], 0)}
              onLoad={() => handleImageLoad(normalizedImages[0], 0)}
              onError={() => handleImageError(normalizedImages[0], 0)}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className={`image-carousel ${className}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="carousel-main">
        <div className="carousel-image-container">
          <img
            src={normalizedImages[currentIndex]}
            alt={`${alt} ${currentIndex + 1}`}
            className={`carousel-image ${imageLoadErrors.has(currentIndex) ? 'error' : ''}`}
            loading={currentIndex === 0 && (className.includes('product-detail') || className.includes('product-image')) ? "eager" : "lazy"}
            decoding="async"
            fetchpriority={currentIndex === 0 && (className.includes('product-detail') || className.includes('product-image')) ? "high" : "auto"}
            onClick={() => onImageClick && onImageClick(normalizedImages[currentIndex], currentIndex)}
            onLoad={() => handleImageLoad(normalizedImages[currentIndex], currentIndex)}
            onError={() => handleImageError(normalizedImages[currentIndex], currentIndex)}
          />
          
          {/* Navigation arrows */}
          <button
            className="carousel-nav carousel-nav--prev"
            onClick={goToPrevious}
            aria-label="Previous image"
          >
            <svg viewBox="0 0 24 24" width="24" height="24">
              <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" fill="currentColor"/>
            </svg>
          </button>
          
          <button
            className="carousel-nav carousel-nav--next"
            onClick={goToNext}
            aria-label="Next image"
          >
            <svg viewBox="0 0 24 24" width="24" height="24">
              <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" fill="currentColor"/>
            </svg>
          </button>

          {/* Image counter */}
          <div className="carousel-counter">
            {currentIndex + 1} / {normalizedImages.length}
          </div>
        </div>
      </div>

      {/* Thumbnail navigation */}
      {showThumbnails && normalizedImages.length > 1 && (
        <div className="carousel-thumbnails">
          {normalizedImages.map((image, index) => (
            <button
              key={index}
              className={`carousel-thumbnail ${index === currentIndex ? 'active' : ''}`}
              onClick={() => goToSlide(index)}
              aria-label={`Go to image ${index + 1}`}
            >
              <img
                src={image}
                alt={`${alt} thumbnail ${index + 1}`}
                className="thumbnail-image"
                loading="lazy"
                decoding="async"
                onLoad={() => handleImageLoad(image, index)}
                onError={() => handleImageError(image, index)}
              />
            </button>
          ))}
        </div>
      )}

      {/* Dots indicator */}
      {!showThumbnails && normalizedImages.length > 1 && (
        <div className="carousel-dots">
          {normalizedImages.map((_, index) => (
            <button
              key={index}
              className={`carousel-dot ${index === currentIndex ? 'active' : ''}`}
              onClick={() => goToSlide(index)}
              aria-label={`Go to image ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ImageCarousel;
